import { useEffect, useState } from "react";
import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@sportscanner/ui";

type Country = {
  code: string;
  name: string;
  dial: string;
  flag: string;
  // Sample local number (no country code) used as the input placeholder so the
  // user knows the expected format for the currently-selected country.
  example: string;
};

const COUNTRIES: Country[] = [
  {
    code: "RS",
    name: "Srbija",
    dial: "+381",
    flag: "🇷🇸",
    example: "60 1234 567",
  },
  {
    code: "BA",
    name: "Bosna i Hercegovina",
    dial: "+387",
    flag: "🇧🇦",
    example: "61 234 567",
  },
  {
    code: "ME",
    name: "Crna Gora",
    dial: "+382",
    flag: "🇲🇪",
    example: "67 123 456",
  },
  {
    code: "HR",
    name: "Hrvatska",
    dial: "+385",
    flag: "🇭🇷",
    example: "91 234 5678",
  },
  {
    code: "MK",
    name: "Severna Makedonija",
    dial: "+389",
    flag: "🇲🇰",
    example: "70 123 456",
  },
  {
    code: "SI",
    name: "Slovenija",
    dial: "+386",
    flag: "🇸🇮",
    example: "31 234 567",
  },
  {
    code: "AL",
    name: "Albanija",
    dial: "+355",
    flag: "🇦🇱",
    example: "67 212 3456",
  },
  {
    code: "BG",
    name: "Bugarska",
    dial: "+359",
    flag: "🇧🇬",
    example: "87 123 4567",
  },
  {
    code: "RO",
    name: "Rumunija",
    dial: "+40",
    flag: "🇷🇴",
    example: "712 345 678",
  },
  {
    code: "HU",
    name: "Mađarska",
    dial: "+36",
    flag: "🇭🇺",
    example: "20 123 4567",
  },
  {
    code: "AT",
    name: "Austrija",
    dial: "+43",
    flag: "🇦🇹",
    example: "664 123 4567",
  },
  {
    code: "DE",
    name: "Nemačka",
    dial: "+49",
    flag: "🇩🇪",
    example: "1512 3456789",
  },
  {
    code: "CH",
    name: "Švajcarska",
    dial: "+41",
    flag: "🇨🇭",
    example: "78 123 45 67",
  },
  {
    code: "FR",
    name: "Francuska",
    dial: "+33",
    flag: "🇫🇷",
    example: "6 12 34 56 78",
  },
  {
    code: "IT",
    name: "Italija",
    dial: "+39",
    flag: "🇮🇹",
    example: "312 345 6789",
  },
  {
    code: "ES",
    name: "Španija",
    dial: "+34",
    flag: "🇪🇸",
    example: "612 345 678",
  },
  {
    code: "NL",
    name: "Holandija",
    dial: "+31",
    flag: "🇳🇱",
    example: "6 12345678",
  },
  {
    code: "GB",
    name: "Velika Britanija",
    dial: "+44",
    flag: "🇬🇧",
    example: "7700 900123",
  },
  { code: "US", name: "SAD", dial: "+1", flag: "🇺🇸", example: "555 123 4567" },
  {
    code: "RU",
    name: "Rusija",
    dial: "+7",
    flag: "🇷🇺",
    example: "912 345 67 89",
  },
  {
    code: "TR",
    name: "Turska",
    dial: "+90",
    flag: "🇹🇷",
    example: "532 123 4567",
  },
];

const DEFAULT_COUNTRY = "RS";

const COUNTRIES_BY_DIAL_LENGTH = [...COUNTRIES].sort(
  (a, b) => b.dial.length - a.dial.length,
);

function splitValue(value: string): { code: string; local: string } {
  const trimmed = value.trim();
  if (!trimmed) return { code: DEFAULT_COUNTRY, local: "" };
  if (trimmed.startsWith("+")) {
    const match = COUNTRIES_BY_DIAL_LENGTH.find((c) =>
      trimmed.startsWith(c.dial),
    );
    if (match) {
      return { code: match.code, local: trimmed.slice(match.dial.length) };
    }
  }
  return { code: DEFAULT_COUNTRY, local: trimmed };
}

interface PhoneInputProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  invalid?: boolean;
  className?: string;
  // When false the country selector is hidden and the field becomes a plain
  // local-number input. Used when the club doesn't send SMS, so a country
  // dial code isn't strictly necessary on the booking form.
  showCountrySelector?: boolean;
}

export default function PhoneInput({
  id,
  value,
  onChange,
  onBlur,
  disabled,
  invalid,
  className,
  showCountrySelector = true,
}: PhoneInputProps) {
  const initial = splitValue(value);
  const [countryCode, setCountryCode] = useState(initial.code);
  const [local, setLocal] = useState(initial.local);

  useEffect(() => {
    const next = splitValue(value);
    setLocal(next.local);
    if (value.trim().startsWith("+")) {
      setCountryCode(next.code);
    }
  }, [value]);

  const country = COUNTRIES.find((c) => c.code === countryCode) ?? COUNTRIES[0];

  if (!showCountrySelector) {
    // Plain local-number input (no dial code): show the full national format
    // with the trunk "0" prefix, e.g. "060 1234 567" for Serbia. With the
    // selector the dial code is separate, so the example drops the leading 0.
    return (
      <Input
        id={id}
        type="tel"
        inputMode="numeric"
        autoComplete="tel"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={`0${country.example}`}
        disabled={disabled}
        className={`rounded-lg border-neutral-200 focus:border-primary-500 ${invalid ? "border-red-400" : ""} ${className ?? ""}`}
      />
    );
  }

  const emit = (code: string, localPart: string) => {
    const dial = COUNTRIES.find((c) => c.code === code)?.dial ?? "";
    onChange(localPart ? `${dial}${localPart}` : "");
  };

  return (
    <div
      className={`flex items-stretch gap-2 ${className ?? ""}`}
      onBlur={onBlur}
    >
      <Select
        value={countryCode}
        onValueChange={(v) => {
          setCountryCode(v);
          emit(v, local);
        }}
        disabled={disabled}
      >
        <SelectTrigger
          className={`w-[7.5rem] rounded-lg border-neutral-200 focus:border-primary-500 ${invalid ? "border-red-400" : ""}`}
        >
          <SelectValue>
            <span className="flex items-center gap-1.5 text-sm">
              <span className="text-base leading-none">{country.flag}</span>
              <span>{country.dial}</span>
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {COUNTRIES.map((c) => (
            <SelectItem key={c.code} value={c.code}>
              <span className="flex items-center gap-2">
                <span className="text-base leading-none">{c.flag}</span>
                <span>{c.name}</span>
                <span className="text-neutral-400">{c.dial}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        id={id}
        type="tel"
        inputMode="numeric"
        autoComplete="tel-national"
        value={local}
        onChange={(e) => {
          const next = e.target.value.replace(/[^\d\s-]/g, "");
          setLocal(next);
          emit(countryCode, next);
        }}
        placeholder={country.example}
        disabled={disabled}
        className={`flex-1 rounded-lg border-neutral-200 focus:border-primary-500 ${invalid ? "border-red-400" : ""}`}
      />
    </div>
  );
}
